(() => {
  const ROOT_SELECTOR = '[data-p11-guide]';

  const slugify = (value) => value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'section';

  const writeClipboard = async (value) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  };

  const initializeGuide = (root) => {
    if (!root || root.dataset.p11GuideInitialized === 'true') return;
    root.dataset.p11GuideInitialized = 'true';

    const content = root.querySelector('[data-p11-article-content]');
    const tocLists = [...root.querySelectorAll('[data-p11-toc-list]')];
    const headings = content
      ? [...content.querySelectorAll('h2')].filter((heading) => !heading.closest('.article-key-takeaways'))
      : [];
    const usedIds = new Set([...document.querySelectorAll('[id]')].map((node) => node.id));

    headings.forEach((heading, index) => {
      if (!heading.id) {
        const base = slugify(heading.textContent);
        let candidate = base;
        let suffix = 2;
        while (usedIds.has(candidate)) {
          candidate = `${base}-${suffix}`;
          suffix += 1;
        }
        heading.id = candidate;
        usedIds.add(candidate);
      }

      tocLists.forEach((list) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const number = document.createElement('span');
        const label = document.createElement('span');
        item.dataset.level = '2';
        link.href = `#${heading.id}`;
        number.setAttribute('aria-hidden', 'true');
        number.textContent = String(index + 1).padStart(2, '0');
        label.className = 'p11-guide-toc__label';
        label.textContent = heading.textContent.trim();
        link.append(number, label);
        link.dataset.p11TocIndex = String(index);
        item.appendChild(link);
        list.appendChild(item);
      });
    });

    if (!headings.length) {
      root.querySelectorAll('.p11-guide-toc').forEach((toc) => { toc.hidden = true; });
    } else {
      const allTocLinks = [...root.querySelectorAll('[data-p11-toc-list] a')];
      const setCurrent = (index) => {
        allTocLinks.forEach((link) => {
          if (Number(link.dataset.p11TocIndex) === index) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      };

      setCurrent(0);
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) setCurrent(headings.indexOf(visible[0].target));
        }, { rootMargin: '-18% 0px -68% 0px', threshold: [0, 1] });
        headings.forEach((heading) => observer.observe(heading));
      }

      allTocLinks.forEach((link) => {
        link.addEventListener('click', () => {
          const mobileToc = root.querySelector('[data-p11-mobile-toc]');
          if (mobileToc && window.matchMedia('(max-width: 900px)').matches) mobileToc.open = false;
        });
      });
    }

    if (content) {
      content.querySelectorAll('table').forEach((table) => {
        if (table.parentElement.classList.contains('p11-guide__table-scroll')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'p11-guide__table-scroll';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', table.getAttribute('aria-label') || 'Scrollable article table');
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
    }

    const signup = root.querySelector('[data-p11-signup]');
    const signupToggle = root.querySelector('[data-p11-signup-toggle]');
    const signupStatus = root.querySelector('[data-p11-signup-status]');
    const headerSection = document.querySelector('.elevenp-header-section');
    const updateSignupOffset = () => {
      if (!signup) return;
      if (!window.matchMedia('(max-width: 767px)').matches || !headerSection) {
        signup.style.removeProperty('--p11-guide-mobile-signup-top');
        return;
      }

      const headerBottom = Math.max(0, Math.round(headerSection.getBoundingClientRect().bottom));
      signup.style.setProperty('--p11-guide-mobile-signup-top', `${headerBottom}px`);
    };
    const closeSignup = () => {
      if (!signup || !signupToggle) return;
      signup.classList.remove('is-expanded');
      signupToggle.setAttribute('aria-expanded', 'false');
    };

    if (signup && signupToggle) {
      signupToggle.addEventListener('click', () => {
        const expanded = signup.classList.toggle('is-expanded');
        signupToggle.setAttribute('aria-expanded', String(expanded));
      });
    }

    updateSignupOffset();
    window.addEventListener('resize', updateSignupOffset, { passive: true });
    if ('ResizeObserver' in window && headerSection) {
      const headerObserver = new ResizeObserver(updateSignupOffset);
      headerObserver.observe(headerSection);
    }

    root.querySelectorAll('[data-p11-signup-pending]').forEach((button) => {
      button.addEventListener('click', () => {
        if (signupStatus) signupStatus.textContent = signupStatus.dataset.pendingMessage || 'Newsletter sign-up is not connected yet.';
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeSignup();
    });

    const shareStatus = root.querySelector('[data-p11-share-status]');
    const shareUrl = root.dataset.shareUrl || window.location.href;
    const shareTitle = root.dataset.shareTitle || document.title;

    root.querySelectorAll('[data-p11-share-copy]').forEach((button) => {
      button.addEventListener('click', async () => {
        try {
          await writeClipboard(shareUrl);
          if (shareStatus) shareStatus.textContent = 'Article link copied.';
        } catch (_) {
          if (shareStatus) shareStatus.textContent = 'Copy the link from your browser address bar.';
        }
      });
    });

    root.querySelectorAll('[data-p11-share-native]').forEach((button) => {
      button.addEventListener('click', async () => {
        try {
          if (navigator.share) {
            await navigator.share({ title: shareTitle, url: shareUrl });
            return;
          }
          await writeClipboard(shareUrl);
          if (shareStatus) shareStatus.textContent = 'Article link copied. Paste it into Instagram.';
        } catch (error) {
          if (error && error.name === 'AbortError') return;
          if (shareStatus) shareStatus.textContent = 'Use your browser share menu to continue.';
        }
      });
    });

    const rail = root.querySelector('[data-p11-guide-rail]');
    const updateRailStickyOffset = () => {
      if (!rail) return;
      if (window.matchMedia('(max-width: 900px)').matches) {
        rail.style.removeProperty('--p11-guide-rail-top');
        return;
      }
      const bottomGap = 24;
      const nextTop = Math.min(24, window.innerHeight - rail.offsetHeight - bottomGap);
      rail.style.setProperty('--p11-guide-rail-top', `${nextTop}px`);
    };

    updateRailStickyOffset();
    window.addEventListener('resize', updateRailStickyOffset, { passive: true });
    if ('ResizeObserver' in window && rail) {
      const railObserver = new ResizeObserver(updateRailStickyOffset);
      railObserver.observe(rail);
    }
  };

  const initializeAll = (scope = document) => {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initializeGuide(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initializeGuide);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
